import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight, HeartCrack } from 'lucide-react';
import classNames from 'classnames';
import { PRODUCTS_PER_PAGE } from '@lambda/apis/src/catalog-v2/constants';
import { ProductT, SkuT } from '@lambda/apis/src/catalog-v2/types';
import i18n from '@/settings/i18n';
import { useCollectionPageContext } from '@/components/collections-v3/hooks/useCollectionPageContext';
import ProductCard from '@/components/v2/common/card/ProductCard';
import GamingCard from '@/components/v2/common/card/GamingCard';
import OfferCard from '@/components/v2/common/card/OfferCard';
import { generateSavingsPercentage } from '@/components/v2/product/hooks/helpers';
import { generateOfferImage } from '@/components/v2/product/sections/main/left/image/helpers';

const t = i18n.init('collections');

// Builds a windowed list of page numbers with `null` gaps for ellipsis. Keeps a
// 3-page window around the current page, clamped to the edges — so page 1 shows
// 1·2·3·…·N and the last page shows 1·…·N-2·N-1·N.
const getPageItems = (current: number, count: number): (number | null)[] => {
  if (count <= 7) {
    return Array.from({ length: count }, (_, i) => i + 1);
  }

  let start = current - 1;
  let end = current + 1;

  if (start < 1) {
    start = 1;
    end = 3;
  }

  if (end > count) {
    end = count;
    start = count - 2;
  }

  const window: number[] = [];

  for (let page = start; page <= end; page += 1) {
    window.push(page);
  }

  const sorted = [...new Set([1, ...window, count])].sort((a, b) => a - b);

  const items: (number | null)[] = [];

  sorted.forEach((page, index) => {
    if (index > 0 && page - sorted[index - 1] > 1) {
      items.push(null);
    }

    items.push(page);
  });

  return items;
};

const ARROW_CLASS =
  'flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 transition-colors hover:border-gray-700';

// Renders crawlable <a href> links (good for SEO) but navigates client-side via
// goToPage, which fetches the page, syncs the URL and scrolls to the top.
const PageLink = ({
  page,
  disabled,
  ariaLabel,
  ariaCurrent,
  className,
  children,
}: {
  page: number;
  disabled?: boolean;
  ariaLabel?: string;
  ariaCurrent?: boolean;
  className: string;
  children: React.ReactNode;
}) => {
  const { goToPage, getPageHref } = useCollectionPageContext();

  if (disabled) {
    return (
      <span
        aria-disabled="true"
        aria-label={ariaLabel}
        className={classNames(className, 'cursor-not-allowed opacity-40')}
      >
        {children}
      </span>
    );
  }

  return (
    <Link
      href={getPageHref(page)}
      scroll={false}
      aria-label={ariaLabel}
      aria-current={ariaCurrent ? 'page' : undefined}
      className={className}
      onClick={(event) => {
        // Keep the href crawlable but navigate client-side.
        event.preventDefault();
        goToPage(page);
      }}
    >
      {children}
    </Link>
  );
};

const Pagination = () => {
  const { currentPage, pageCount, isLoadingMore } = useCollectionPageContext();

  if (pageCount <= 1) {
    return null;
  }

  const pageItems = getPageItems(currentPage, pageCount);

  return (
    <nav
      className="mt-8 flex items-center justify-center gap-1"
      aria-label="Pagination"
    >
      <PageLink
        page={currentPage - 1}
        disabled={currentPage === 1 || isLoadingMore}
        ariaLabel={t`Previous page`}
        className={ARROW_CLASS}
      >
        <ChevronLeft className="h-4 w-4" />
      </PageLink>
      {pageItems.map((page, index) =>
        page === null ? (
          // eslint-disable-next-line react/no-array-index-key
          <span key={`gap-${index}`} className="px-1 text-gray-700/60">
            …
          </span>
        ) : (
          <PageLink
            key={page}
            page={page}
            disabled={isLoadingMore}
            ariaCurrent={page === currentPage}
            className={classNames(
              'flex h-9 min-w-9 items-center justify-center rounded-md border px-2 text-sm transition-colors',
              {
                'border-gray-700 bg-gray-700 font-medium text-white':
                  page === currentPage,
                'border-gray-200 hover:border-gray-700': page !== currentPage,
              },
            )}
          >
            {page}
          </PageLink>
        ),
      )}
      <PageLink
        page={currentPage + 1}
        disabled={currentPage === pageCount || isLoadingMore}
        ariaLabel={t`Next page`}
        className={ARROW_CLASS}
      >
        <ChevronRight className="h-4 w-4" />
      </PageLink>
    </nav>
  );
};

const getColumnClassName = (isDealsPage: boolean) =>
  isDealsPage
    ? 'xxs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4'
    : 'grid-cols-2 xs:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4';

// Mirrors the box model of ProductCard / OfferCard so swapping the skeleton for
// real cards doesn't move anything below the grid. Keep in sync with those cards.
const SkeletonCard = ({
  isDealsPage,
  className,
}: {
  isDealsPage: boolean;
  className?: string;
}) =>
  isDealsPage ? (
    <div
      className={classNames(
        '-mb-px -mr-px min-h-[265px] animate-pulse border border-gray-500 bg-gray-700/5',
        className,
      )}
    />
  ) : (
    <div className="-mb-px -mr-px border border-gray-100 p-4">
      <div className="h-[18vw] animate-pulse bg-gray-700/5 xs:h-[13vw] sm:h-[10vw] lg:h-[7vw] xl:h-[95px]" />
      <div className="box-content h-4 animate-pulse bg-gray-700/5 pt-5 xs:pt-6" />
      <div className="mt-1 h-4 sm:h-5" />
    </div>
  );

// The gaming card sits at index 5 on mobile, 8 from sm and 11 from xl. It is
// placed with CSS rather than the window width so the server-rendered grid
// already has it in the right place.
const getGamingCardIndexes = (length: number) => {
  const lastIndex = length - 1;

  return {
    mobile: Math.min(5, lastIndex),
    sm: Math.min(8, lastIndex),
    xl: Math.min(11, lastIndex),
  };
};

const getGamingCardClassName = (
  index: number,
  indexes: ReturnType<typeof getGamingCardIndexes>,
) =>
  classNames({
    hidden: index !== indexes.mobile,
    'sm:hidden': index === indexes.mobile && index !== indexes.sm,
    'sm:flex': index !== indexes.mobile && index === indexes.sm,
    'xl:hidden': index === indexes.sm && index !== indexes.xl,
    'xl:flex': index !== indexes.sm && index === indexes.xl,
  });

const Grid = () => {
  const {
    type,
    products,
    isApplyingFilters,
    selectedFilters,
    isLoadingMore,
    pageCount,
  } = useCollectionPageContext();

  const isDealsPage = type === 'MS_CATALOG_DEALS';

  // A page change swaps the list wholesale, so hold the old height through the
  // swap and release once the smooth scroll to the top has finished (anything
  // that shifts after that is off-screen).
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;

    if (isLoadingMore) {
      if (wrapper) {
        wrapper.style.minHeight = `${wrapper.offsetHeight}px`;
      }

      return undefined;
    }

    const release = () => {
      if (wrapper) {
        wrapper.style.minHeight = '';
      }
    };
    const timeout = window.setTimeout(release, 1000);

    window.addEventListener('scrollend', release, { once: true });

    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('scrollend', release);
    };
  }, [isLoadingMore]);

  const hasVariantFilters = Object.values(selectedFilters?.variants ?? {}).some(
    (values) => values.length > 0,
  );

  const gamingCardIndexes = getGamingCardIndexes(products.length);

  // Loading state
  if (isApplyingFilters) {
    // `products` is still the previous result here, so place the skeleton's
    // gaming card for a full page rather than for whatever was shown before.
    const skeletonGamingCardIndexes = getGamingCardIndexes(PRODUCTS_PER_PAGE);

    return (
      <div>
        <div className={classNames('grid', getColumnClassName(isDealsPage))}>
          {Array.from({ length: PRODUCTS_PER_PAGE }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={index}>
              {isDealsPage &&
                Object.values(skeletonGamingCardIndexes).includes(index) && (
                  <SkeletonCard
                    isDealsPage={isDealsPage}
                    className={getGamingCardClassName(
                      index,
                      skeletonGamingCardIndexes,
                    )}
                  />
                )}
              <SkeletonCard isDealsPage={isDealsPage} />
            </React.Fragment>
          ))}
        </div>
        {pageCount > 1 && <div className="mt-8 h-9" />}
      </div>
    );
  }

  // Empty state
  if (products.length === 0) {
    return (
      <div className="flex items-center justify-center gap-2 p-4 text-base">
        <HeartCrack className="h-6 w-6 shrink-0" strokeWidth={1.5} />
        {t`Sorry, no products matched your selection.`}
      </div>
    );
  }

  return (
    <div key="grid" ref={wrapperRef}>
      <div
        className={classNames(
          'grid transition-opacity',
          getColumnClassName(isDealsPage),
          { 'pointer-events-none opacity-50': isLoadingMore },
        )}
      >
        {products.map((product, index) => {
          if (isDealsPage) {
            const sku = product as SkuT;
            const offer = sku?.offers?.[0];
            const skuProduct = sku?.product;

            if (!offer || !sku || !skuProduct) {
              return null;
            }

            const compareAtPrice = sku.compareAtPrice ?? null;

            const percentageSaving = generateSavingsPercentage(
              offer.price,
              compareAtPrice,
            );

            const isFeatured = !!offer?.featured;

            return (
              <React.Fragment key={`deal-${sku.id}`}>
                {Object.values(gamingCardIndexes).includes(index) && (
                  <GamingCard
                    className={getGamingCardClassName(index, gamingCardIndexes)}
                  />
                )}
                <OfferCard
                  index={index}
                  link={`${encodeURIComponent(
                    skuProduct.slug,
                  )}?skuId=${encodeURIComponent(sku.id)}`}
                  image={generateOfferImage(sku, skuProduct)}
                  title={sku.title}
                  compareAtPrice={compareAtPrice ? compareAtPrice / 100 : null}
                  percentageSaving={percentageSaving}
                  price={offer.price / 100}
                  featured={isFeatured}
                  className="-mb-px -mr-px"
                />
              </React.Fragment>
            );
          }

          return (
            <ProductCard
              index={index}
              key={product.id}
              product={product as ProductT}
              includeSkuId={hasVariantFilters}
              className="-mb-px -mr-px border border-gray-100"
            />
          );
        })}
      </div>

      <Pagination />
    </div>
  );
};

export default Grid;
